import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, FileText } from "lucide-react";
import { company } from "@/data/site";
import { useLang } from "@/context/LanguageContext";
import { useT } from "@/i18n";

const linkDefs = [
  { to: "/", key: "nav.home", end: true, testid: "anasayfa" },
  { to: "/kurumsal", key: "nav.about", testid: "kurumsal" },
  { to: "/hizmetler", key: "nav.services", testid: "hizmetler" },
  { to: "/projeler", key: "nav.projects", testid: "projeler" },
  { to: "/iletisim", key: "nav.contact", testid: "iletisim" },
];

function LangSwitch({ solid }) {
  const { lang, setLang } = useLang();
  const base = "font-mono text-[12px] tracking-wide transition-colors duration-200";
  const activeCls = solid ? "text-ink font-semibold" : "text-white font-semibold";
  const idleCls = solid
    ? "text-ink/40 hover:text-ink"
    : "text-white/50 hover:text-white";
  return (
    <div className="flex items-center gap-1.5" data-testid="lang-switch">
      <button
        type="button"
        onClick={() => setLang("tr")}
        data-testid="lang-tr"
        aria-pressed={lang === "tr"}
        className={`${base} ${lang === "tr" ? activeCls : idleCls}`}
      >
        TR
      </button>
      <span className={solid ? "text-ink/25" : "text-white/30"}>/</span>
      <button
        type="button"
        onClick={() => setLang("en")}
        data-testid="lang-en"
        aria-pressed={lang === "en"}
        className={`${base} ${lang === "en" ? activeCls : idleCls}`}
      >
        EN
      </button>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const t = useT();
  const { lang, setLang } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const solid = scrolled || open;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        solid
          ? "bg-white/95 backdrop-blur-xl border-b border-ink/10 shadow-[0_10px_40px_-24px_rgba(10,22,38,0.4)]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className={`container-kyt flex items-center justify-between transition-all duration-500 ${solid ? "h-[72px]" : "h-[94px]"}`}>
        <Link to="/" className="flex items-center" data-testid="navbar-logo">
          <img
            src={solid ? "/logo.png" : "/logo-white.png"}
            alt="KYT Yapı Teknolojileri"
            className={`w-auto transition-all duration-500 ${solid ? "h-10" : "h-12 lg:h-14"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {linkDefs.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.testid}`}
              className={({ isActive }) =>
                `relative text-[13.5px] tracking-wide transition-colors duration-300 py-2 ${
                  solid
                    ? isActive ? "text-ink" : "text-ink/55 hover:text-ink"
                    : isActive ? "text-white" : "text-white/70 hover:text-white"
                } after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-full after:origin-left after:transition-transform after:duration-300 ${
                  solid ? "after:bg-brand-blue" : "after:bg-white"
                } ${isActive ? "after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"}`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden xl:flex">
            <LangSwitch solid={solid} />
          </div>

          <a
            href={company.catalog}
            download
            data-testid="navbar-catalog"
            className={`hidden lg:inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[12.5px] tracking-wide font-medium transition-all duration-300 hover:-translate-y-0.5 ${
              solid
                ? "border border-ink/20 text-ink hover:bg-ink hover:text-white"
                : "border border-white/45 text-white hover:bg-white hover:text-navy"
            }`}
          >
            <FileText className="h-4 w-4" /> {t("common.catalogOnline")}
          </a>

          <a
            href={`tel:${company.phoneHref}`}
            data-testid="navbar-phone"
            className="hidden lg:inline-flex items-center gap-2 rounded-full bg-brand-blue px-4 py-2.5 text-[12.5px] tracking-wide font-semibold text-white transition-all duration-300 hover:bg-navy hover:-translate-y-0.5"
          >
            <Phone className="h-4 w-4" /> {company.phone}
          </a>

          <button
            className={`lg:hidden p-1.5 transition-colors ${solid ? "text-ink" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-ink/10" data-testid="mobile-menu">
          <nav className="container-kyt py-4 flex flex-col">
            {linkDefs.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`mobile-nav-${l.testid}`}
                className={({ isActive }) =>
                  `py-3.5 border-b border-ink/8 text-[15px] tracking-wide ${isActive ? "text-brand-blue" : "text-ink/80"}`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
            <a href={`tel:${company.phoneHref}`} data-testid="mobile-phone" className="mt-4 inline-flex w-max items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-[14px] font-semibold text-white">
              <Phone className="h-4 w-4" /> {company.phone}
            </a>
            <a href={company.catalog} download data-testid="mobile-catalog" className="mt-3 inline-flex w-max items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-[14px] font-medium text-ink">
              <FileText className="h-4 w-4" /> {t("common.catalogOnline")}
            </a>
            <div className="mt-5 flex items-center gap-2" data-testid="mobile-lang-switch">
              <button
                type="button"
                onClick={() => setLang("tr")}
                data-testid="mobile-lang-tr"
                className={`font-mono text-[13px] tracking-wide px-3 py-1.5 rounded-full border transition-colors ${lang === "tr" ? "border-brand-blue text-brand-blue" : "border-ink/15 text-ink/50"}`}
              >
                TR
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`font-mono text-[13px] tracking-wide px-3 py-1.5 rounded-full border transition-colors ${lang === "en" ? "border-brand-blue text-brand-blue" : "border-ink/15 text-ink/50"}`}
              >
                EN
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
